import { Arr, memone } from '@deqo/foundation';
import { darken, tint } from 'polished';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Images } from '../../../@resources/img';
import { tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Host } from '../../../concepts/objects/ObjectSymbols';
import { Icon } from '../../../concepts/resources/Icon';
import { Point } from '../../../platform/geom/Point';
import { Fill } from '../../../ui/Fill';
import { useResolve } from '../../Container';
import { ValueCard } from '../../packages/hand/ValueCard';
import { Participant } from '../participants/Participant';
import { ParticipantManager } from '../participants/ParticipantManager';
import { Colors } from './../../../ui';
import { aspect } from './Aspect';
import { Emoticon } from './Emoticon';
import { RenderTextField } from './RenderField';
import {
    RenderBounds,
    RenderBox,
    RenderConcealed,
    RenderCounter,
    RenderGraffiti,
    RenderHeadline,
    RenderInner,
    useBracketShortcut,
    usePose
} from './RenderHelpers';
import { VisualBase } from './VisualBase';

const useParticipant = (userId: string) => {
    return useResolve(ParticipantManager).get(userId);
};

const RenderEmoticon: LFC<{ value: string }> = ({ value }) => {
    return <RenderBox
        inset="0"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Icon src={`deqo://icons/tokens/${value}@[0.75,0.75,0]`} />
    </RenderBox>;
};

const RenderImage = styled.img`
    display: block;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-size: cover;
`;

const RenderInitials = styled.div<{ color: string }>`
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Rubik;
    font-size: 34px;
    font-weight: 450;
    color: white;
    background: ${p => p.color};
    border-radius: 50%;
`;

const RenderUser: LFC<{ value: Participant }> = ({ value }) => {
    return <RenderBox inset="0" display="flex" alignItems="center" justifyContent="center">
        {!!value.photo ? <RenderImage src={value.photo} /> : <RenderInitials color={value.color}>{Participant.toInitials(value.name)}</RenderInitials>}
    </RenderBox>;
};

const RenderUrl: LFC<{ value: string }> = ({ value }) => {
    return <RenderBox inset="0" display="flex" alignItems="center" justifyContent="center">
        <RenderImage src={value} />
    </RenderBox>;
};

const RenderFront: LFC<{ object: ValueCard }> = ({ object, children }) => {
    const { selected, targeted } = usePose(object);
    return <RenderBox
        inset="0"
        borderRadius="8px"
        color={tintSafe(selected ? 0 : (targeted ? .05 : .1), object.color)}
        backgroundColor="white"
        transform={object.concealed ? 'rotateY(180deg)' : ''}
        backfaceVisibility="hidden"
        transition="transform 200ms ease-in-out"
    >
        <RenderBox
            inset="0"
            border={`solid 3px ${selected || targeted ? darken(.05, Colors.primaryLight) : Colors.primaryLight}`}
            borderRadius="8px" />
        {children}

    </RenderBox>;
};

const RenderBack: LFC<{ object: ValueCard }> = ({ object, children }) => {
    const { selected, targeted } = usePose(object);
    // 009688
    return <RenderBox
        inset="0"
        border={`solid 3px ${selected || targeted ? darken(.05, Colors.primaryLight) : Colors.primaryLight}`}
        borderRadius="8px"
        // background={`${tintSafe(.5, Colors.primary)} url(${Images.LogoWhite}) no-repeat center center`}
        background={`${tint(.75, Colors.primaryLight)} url(${Images.CardBack}) no-repeat center center`}
        backgroundSize="48px 48px"
        transform={object.concealed ? '' : 'rotateY(180deg)'}
        backfaceVisibility="hidden"
        transition="transform 200ms ease-in-out"
    >
        <RenderBox inset="0px" border={'solid 3px white'} borderRadius="5px" />
        {children}
    </RenderBox>;
};

export const ValueCardVisual: LFC<{ object: ValueCard }> = ({ object, children }) => {

    const user = useParticipant(object.userId);
    const bs = useBracketShortcut(object.largeText);
    const { beingEdited, selected, targeted, effects } = usePose(object);

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderFront object={object}>
                    {!beingEdited && <>
                        <RenderTextField object={object} name="smallText" />
                        <RenderFlipped>
                            <RenderTextField object={object} name="smallText" />
                        </RenderFlipped>
                        {bs.type === 'none' && <RenderTextField object={object} name="largeText" />}
                        {bs.type === 'emoticon' && <RenderEmoticon value={bs.value} />}
                        {bs.type === 'user' && <RenderUser value={bs.value} />}
                        {bs.type === 'url' && <RenderUrl value={bs.value} />}
                    </>}
                </RenderFront>
                <RenderBack object={object} />
                {(selected || targeted) && !beingEdited && !effects?.shuffle && user && !user.self &&
                    <RenderHeadline>
                        <RenderCounter user={user} />
                    </RenderHeadline>
                }
                {(selected || targeted) && !beingEdited && !effects?.shuffle && user?.self &&
                    <RenderHeadline>
                        <RenderConcealed object={object} />
                    </RenderHeadline>
                }
            </RenderInner>
        </RenderBounds>
    </>;
};

/* eslint-disable max-len */
/* eslint-disable no-mixed-operators */
export class ValueCardVisualX extends VisualBase<ValueCard> {

    @aspect()
    private get isHover() {
        const { interaction, isPressed } = this;
        return interaction?.type === 'invoke' && !isPressed;
    }

    @aspect()
    private get isActive() {
        const { interaction, isPressed } = this;
        return interaction?.type === 'invoke' && isPressed;
    }

    @aspect()
    private get shuffleValue() {
        const value = this.pose.effects?.shuffle?.value as number;
        if (value !== undefined && value !== null) {
            return Math.floor(value * shuffleTransforms.length);
        }
        return null;
    }

    private get frameStyling() {
        const { object } = this;
        return {
            width: object.width,
            height: object.height,
            perspective: '160rem',
        };
    }

    @aspect()
    private get shadow() {
        // Compute shadow angle for rotation
        const pt = new Point(0, 3).rotate(this.object.r * -1);
        return `
            ${pt.x}px ${pt.y}px 10px 0 rgba(0, 0, 0, 0.1),
        `;
    }

    private get backStyling(): any {
        const { object } = this;
        return {
            transform: object.concealed ? '' : 'rotateY(180deg)',
            backfaceVisibility: 'hidden',
        };
    }

    private get borderStyling(): any {
        const { object } = this;
        const fore = tintSafe(this.isSelected ? 0 : (this.isHighlighted ? .05 : .1), object.color);
        const back = tintSafe(.98, object.color);
        return {
            position: 'relative',
            width: object.width,
            height: object.height,
            backgroundColor: back,
            borderRadius: '6px',
            boxShadow: this.shadow,
            color: fore,
            transform: object.concealed ? 'rotateY(180deg)' : '',
            backfaceVisibility: 'hidden',
            transition: 'transform 200ms ease-in-out',
        };
    }

    private get highlightStyling(): any {
        const blend = this.isSelected ? 0 : (this.isHighlighted ? .2 : 0.4);
        return {
            borderColor: tintSafe(blend, this.object.color),
        };
    }

    @aspect()
    private get iconStyling(): any {
        return {
            // color: this.object.color,
        };
    }

    @aspect()
    private get iconSrc(): string {
        const icon = Emoticon.lookup(this.object.largeText);
        return !!icon ? `deqo://icons/tokens/${icon}@[0.75,0.75,0]` : null;
    }

    private get user() {
        return this.resolveUser(this.object.userId);
    }

    public render() {
        return <RenderBounds object={this.object} style={this.frameStyling}>
            <RenderShuffle shuffle={this.shuffleValue}>
                <div style={this.borderStyling}>
                    <RenderGraffiti object={this.object} />
                    {!this.isBeingEdited && <>
                        <RenderTextField object={this.object} name="smallText" />
                        <RenderFlipped>
                            <RenderTextField object={this.object} name="smallText" />
                        </RenderFlipped>
                        {!this.iconSrc && <RenderTextField object={this.object} name="largeText" />}
                        {this.iconSrc && <RenderIcon><Icon src={this.iconSrc} style={this.iconStyling} /></RenderIcon>}
                    </>}
                    <RenderHighlight style={this.highlightStyling} />
                </div>
                <RenderBack2 style={this.backStyling} />
                {this.isHighlighted && !this.isBeingEdited && this.user && !this.user.self &&
                    <RenderHeadline>
                        <RenderCounter user={this.user} />
                    </RenderHeadline>
                }
                {this.isHighlighted && !this.isBeingEdited && this.user?.self &&
                    <RenderHeadline>
                        <RenderConcealed object={this.object} />
                    </RenderHeadline>
                }
            </RenderShuffle>
        </RenderBounds>;
    }

    @memone()
    private resolvePotentialUserFromValue(value: string) {
        const pm = this.props.object[Host].resolve(ParticipantManager);
        let ptpt = null as Participant;
        if (value.match(/\(ME\)/i)) {
            ptpt = pm.get(this.object.userId);
        }
        else if (value.match(/^\((FU|GU|US)\w{8,12}\)/i)) {
            ptpt = pm.get(value.slice(1, -1).toUpperCase());
        }
        if (ptpt) {
            return {
                color: ptpt.color,
                photo: ptpt.photo,
                initials: Participant.toInitials(ptpt.name)
            };
        }
        return null;
    }

    @memone()
    private resolveUser(id: string) {
        if (!id) return null;
        return this.props.object[Host].resolve(ParticipantManager).get(id);
    }
}

// const RenderInner = styled.div`
//     position: absolute;
//     inset: 3px;
//     border: solid 3px white;
// `;

const RenderFlipped = styled(Fill)`
    transform: rotate(180deg);
`;

const shuffleTransform = (value: number) => {
    const ov = new Point(30, 0).rotate(Math.PI * 2 * value);
    return keyframes`
        0% {
            transform: rotate(0deg) translate(0, 0px) rotate(0deg);
        }
        10% {
            transform: rotate(0deg) translate(${ov.x}px, ${ov.y}px) rotate(0deg);
        }
        70% {
            transform: rotate(270deg) translate(${ov.x}px, ${ov.y}px) rotate(-270deg);
        }
        90% {
            transform: rotate(360deg) translate(${ov.x / 2}px, ${ov.y / 2}px) rotate(-360deg);
        }
        100% {
            transform: rotate(360deg) translate(0, 0px) rotate(-360deg);
        }
    `;
};

const shuffleTransforms = new Array(12).fill(0).map((_, i) => shuffleTransform(1 / 12 * i));

const RenderShuffle = styled.div<{ shuffle?: number }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    animation: ${p => (typeof (p.shuffle) === 'number') ? Arr.at(shuffleTransforms, p.shuffle) : 'none'} 1000ms linear;
    animation-direction: ${p => (p.shuffle ?? 0) % 2 === 0 ? 'reverse' : 'forwards'};
`;

const RenderBack2 = styled(Fill)`
    background: ${tintSafe(.15, Colors.primary)} url(${Images.LogoWhite}) no-repeat center center;
    background-size: 48px 48px;
    border: solid 4px ${Colors.primary};
    border-radius: 6px;
    transition: transform 200ms ease-in-out;
`;

const RenderIcon = styled(Fill)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const RenderHighlight = styled(Fill)`
    border: solid 4px;
    border-radius: 6px;
`;
